import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Search, Loader2, CheckCircle2, ExternalLink } from "lucide-react";

interface Deal {
  id: string;
  deal_title: string;
  property_address: string | null;
}

export default function PropertyResearch() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ zillowUrl?: string; dealTitle?: string; timestamp?: number; dealId?: string } | null>(null);
  const { toast } = useToast();

  // Auto-fill property address when deal selection changes
  const handleDealChange = (dealId: string) => {
    setSelectedDealId(dealId);
    setResult(null);
    
    // Find the selected deal and populate the address
    const selectedDeal = deals.find(d => d.id === dealId);
    if (selectedDeal?.property_address) {
      setSearchQuery(selectedDeal.property_address);
    } else {
      setSearchQuery("");
    }
  };

  // Load deals on mount
  useEffect(() => {
    loadDeals();
  }, []);

  const loadDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select('id, deal_title, property_address')
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Error loading deals:", error);
      return;
    }

    setDeals(data || []);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast({
        title: "Error",
        description: "Please enter a property address",
        variant: "destructive",
      });
      return;
    }

    if (!selectedDealId) {
      toast({
        title: "Error",
        description: "Please select a deal to attach this research to",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setResult(null); // Force clear immediately

    try {
      // Save the research
      await supabase.functions.invoke('research-property', {
        body: { query: searchQuery, dealId: selectedDealId }
      });

      // Small delay to ensure database write completes
      await new Promise(resolve => setTimeout(resolve, 500));

      // Now fetch fresh data directly from database
      const { data: freshDealData, error: fetchError } = await supabase
        .from('deals')
        .select('zillow_link, deal_title')
        .eq('id', selectedDealId)
        .single();

      if (fetchError) throw fetchError;

      console.log("=== RESEARCH DEBUG ===");
      console.log("Selected Deal ID:", selectedDealId);
      console.log("Search Query:", searchQuery);
      console.log("Fresh DB Data:", freshDealData);
      console.log("=====================");

      setResult({ 
        zillowUrl: freshDealData.zillow_link,
        dealTitle: freshDealData.deal_title,
        dealId: selectedDealId,
        timestamp: Date.now()
      });
      
      toast({
        title: "Success!",
        description: `Zillow research has been saved to ${freshDealData.deal_title}`,
      });
    } catch (error) {
      console.error("Error researching property:", error);
      toast({
        title: "Error",
        description: "Failed to search property. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Zillow Property Research</h1>
        <p className="text-muted-foreground">
          Search Zillow and save results directly to a deal
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Search Property on Zillow</CardTitle>
          <CardDescription>
            Enter a property address and select which deal to attach the research to
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="deal">Select Deal</Label>
            <Select value={selectedDealId} onValueChange={handleDealChange}>
              <SelectTrigger id="deal">
                <SelectValue placeholder="Choose a deal..." />
              </SelectTrigger>
              <SelectContent>
                {deals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.deal_title} {deal.property_address && `- ${deal.property_address}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Property Address</Label>
            <Input
              id="address"
              placeholder="123 Main St, City, State ZIP"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              disabled={isLoading}
            />
          </div>

          <Button onClick={handleSearch} disabled={isLoading} className="w-full">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Searching Zillow...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Search & Save to Deal
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {result && result.zillowUrl && result.dealId === selectedDealId && (
        <Card key={result.timestamp} className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" />
              Research Saved!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-green-700 mb-4">
              Zillow link has been saved to <strong>{result.dealTitle}</strong>. View it in the deal details.
            </p>
            <a 
              href={result.zillowUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              Open Zillow Link: {result.zillowUrl}
            </a>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
